import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthenticated, user, loading } = useAuth();
  const organizationId = user?.organization?._id;

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || !organizationId) return;

    // socket.io-client is imported here, not at the top of the file.
    //
    // This provider wraps the WHOLE app, so a static import put socket.io and
    // engine.io — about 37 KB of parsed JavaScript — into the main bundle,
    // which every visitor downloads. Only an authenticated user with an
    // organization ever opens a connection, and the only consumer is the
    // dashboard chrome; a visitor reading the marketing page was paying for a
    // socket that the guard above already prevented from connecting.
    let socketInstance;
    let cancelled = false;

    import('socket.io-client').then(({ io }) => {
      // The effect can be torn down while the chunk is still in flight
      // (a fast logout, or React 18 StrictMode's double-invoke). Connecting
      // after that would leak a socket nothing holds a reference to.
      if (cancelled) return;

      socketInstance = io(SOCKET_URL, {
        withCredentials: true,
        transports: ['websocket', 'polling'],
      });

      socketInstance.on('connect', () => {
        setIsConnected(true);
        socketInstance.emit('join-organization', organizationId);
      });

      socketInstance.on('disconnect', () => {
        setIsConnected(false);
      });

      setSocket(socketInstance);
    });

    return () => {
      cancelled = true;
      if (socketInstance) socketInstance.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [isAuthenticated, organizationId, loading]);

  const value = useMemo(() => ({ socket, isConnected }), [socket, isConnected]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
